// Features: M004-F0808 (WebUI #270, milestone M3: the Accept update and replacement link controls)
// Tests: T-M004-0468 (see CalendarUpdateAcceptPanel.test.js beside this file).
//
// Behind two gates: CALENDAR_UPDATE_VIEW_ENABLED and CALENDAR_UPDATE_ACCEPT_ENABLED (design section 8). Both are code constants.
// Renders nothing unless both gates are on, the user holds ledger_manage, and the proposal is pending.
//
// Idempotency: one key per (body) attempt. A retry of the same body (network error, 5xx, one STEP_UP retry) reuses the key.
// A changed body (for example a new reason or a new revision after a link) gets a new key, so the same key never carries a different body.
// A definitive refusal (4xx other than 401) clears the key.
import React, { useEffect, useRef, useState } from 'react';
import StepUpDialog from '../../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import {
  CALENDAR_UPDATE_ACCEPT_ENABLED, CALENDAR_UPDATE_VIEW_ENABLED, validateReason, refusalSentence, refusalCode, needsReplacementLink,
} from './calendarUpdateModel';

const newKey = () => globalThis.crypto?.randomUUID?.() || `cu-${Date.now()}-${Math.random()}`;
const NETWORK = 'The Ledger could not be reached. Check the connection and try again.';
const APPLIED = 'Update applied.';
const CORRECTION = 'A correction review has been opened.';

// Returns the key for this body: the stored one when the body is unchanged, otherwise a fresh one.
const keyFor = (ref, body) => {
  const fingerprint = JSON.stringify(body);
  if (!ref.current || ref.current.fingerprint !== fingerprint) ref.current = { fingerprint, key: newKey() };
  return ref.current.key;
};

// Keep the key on outcomes that may be retried with the same body: no response, a 5xx, or a 401 step-up challenge.
const keepsKey = (failure) => {
  const status = Number(failure?.response?.status);
  return !failure?.response || status >= 500 || status === 401;
};

const failureText = (failure) => (failure?.response ? refusalSentence(failure) : NETWORK);

export default function CalendarUpdateAcceptPanel({
  proposal,
  canManage,
  acceptEnabled = CALENDAR_UPDATE_ACCEPT_ENABLED,
  viewEnabled = CALENDAR_UPDATE_VIEW_ENABLED,
  api,
  orgId,
  onDecided,
}) {
  const [live, setLive] = useState(proposal);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const [notice, setNotice] = useState('');
  const [done, setDone] = useState(false);
  const [stepUp, setStepUp] = useState(null);
  const acceptKey = useRef(null);
  const linkKey = useRef(null);
  const retryAccept = useRef(false);

  useEffect(() => { setLive(proposal); }, [proposal]);

  if (!(viewEnabled && acceptEnabled && canManage && live && live.state === 'pending')) return null;

  const check = validateReason(reason);
  const linkRequired = needsReplacementLink(live);
  const blocked = busy || done || stale || linkRequired || !check.ok;

  const reread = async () => {
    const answer = await api.list(orgId, { proposal_id: live.proposal_id, state: 'any' });
    const found = (answer?.data?.data?.proposals || []).find((row) => row.proposal_id === live.proposal_id);
    if (!found) throw Object.assign(new Error('not found'), { response: { status: 404, data: { error: 'PROPOSAL_NOT_FOUND' } } });
    setLive(found);
    setStale(false);
    return found;
  };

  const reload = async () => {
    setError('');
    try {
      await reread();
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const sendAccept = async (mayStepUp) => {
    const body = { proposal_id: live.proposal_id, proposal_revision: live.proposal_revision, reason: check.reason };
    const key = keyFor(acceptKey, body);
    setBusy(true);
    setError('');
    try {
      const answer = await api.accept(orgId, body, key);
      acceptKey.current = null;
      const result = answer?.data?.data || {};
      setDone(true);
      setNotice(result.outcome === 'correction_review' ? CORRECTION : APPLIED);
      onDecided?.(result);
    } catch (failure) {
      const challenge = mayStepUp ? getStepUpChallengeFromError(failure) : null;
      if (challenge?.id) {
        retryAccept.current = true;
        setStepUp(challenge);
      } else {
        if (!keepsKey(failure)) acceptKey.current = null;
        if (refusalCode(failure) === 'PROPOSAL_REVISION_STALE') setStale(true);
        setError(failureText(failure));
      }
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (decision) => {
    const body = { proposal_id: live.proposal_id, proposal_revision: live.proposal_revision, decision, reason: check.reason };
    const key = keyFor(linkKey, body);
    setBusy(true);
    setError('');
    setStale(true);
    try {
      await api.linkReplacement(orgId, body, key);
      linkKey.current = null;
      await reread();
    } catch (failure) {
      if (!keepsKey(failure)) linkKey.current = null;
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  };

  const completeStepUp = async () => {
    setStepUp(null);
    if (retryAccept.current) {
      retryAccept.current = false;
      await sendAccept(false);
    }
  };

  const cancelStepUp = () => {
    retryAccept.current = false;
    setStepUp(null);
    setError('Verification was cancelled. Nothing was changed.');
  };

  return (
    <section className="calendar-update-accept" aria-label="Accept update">
      <StepUpDialog
        challenge={stepUp}
        title="Verify calendar update"
        description="Confirm your identity before accepting this update."
        onComplete={completeStepUp}
        onCancel={cancelStepUp}
      />
      <label htmlFor="calendar-update-reason">Reason</label>
      <textarea
        id="calendar-update-reason"
        value={reason}
        maxLength={1000}
        onChange={(event) => setReason(event.target.value)}
        disabled={busy || done}
      />

      {linkRequired && (
        <div className="calendar-update-accept__link">
          <p>Choose whether this replacement is linked or separate before accepting.</p>
          <button type="button" disabled={busy || !check.ok} onClick={() => sendLink('link')}>Link to the replacement series</button>
          <button type="button" disabled={busy || !check.ok} onClick={() => sendLink('separate')}>Keep separate</button>
        </div>
      )}

      <button type="button" disabled={blocked} onClick={() => sendAccept(true)}>Accept update</button>

      {stale && !busy && (
        <button type="button" onClick={reload}>Reload</button>
      )}
      {error && <p role="alert" className="calendar-update-accept__error">{error}</p>}
      {notice && <p role="status" className="calendar-update-accept__notice">{notice}</p>}
    </section>
  );
}
